import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { AppSidebar } from '@/components/app-sidebar';
import { DemoBanner } from '@/components/demo-banner';
import { GlobalDrawers } from '@/components/global-drawers';
import { Toaster } from '@/components/toast';
import { TopBar } from '@/components/top-bar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { auth } from '@/lib/auth';
import { useClients, useDemoPersona, useMe } from '@/lib/clients';
import { useHotkeys } from '@/lib/hotkeys';
import { setOrgId, useOrgId } from '@/lib/org';
import { notifyOrgLocale, setOrgLocale } from '@/lib/org-locale';

/** Pages anyone can open: the home page, sign in, sign up, the demo and the sign-in callback. */
export const isPublicPath = (pathname: string): boolean =>
  pathname === '/' ||
  pathname === '/login' ||
  pathname === '/signup' ||
  pathname === '/demo' ||
  pathname.startsWith('/auth/');

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: async ({ location }) => {
    if (!auth.enabled || isPublicPath(location.pathname)) return;
    if (!(await auth.getUser())) {
      // an expired demo session goes back into the demo, not to Log in
      const demo = await auth.wasDemo();
      if (demo) throw redirect({ to: '/demo', search: { as: demo } });
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

function AppShell() {
  // Public pages and the sign-in callback must not mount the shell: its queries would run
  // before a token exists.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (isPublicPath(pathname)) return <Outlet />;
  return <Shell />;
}

function Shell() {
  const me = useMe(auth.enabled);
  // the open client's currency and number grouping (INR in lakhs and crores for India)
  const orgId = useOrgId();
  const clients = useClients(auth.enabled).data;
  const client = clients?.find((c) => c.id === orgId) ?? null;
  // The public demo (ADR-0020) opens on its first client (the Ganga Traders copy), also when this
  // browser last had a client the demo cannot see.
  const demo = useDemoPersona();
  useEffect(() => {
    if (demo && clients?.length && !clients.some((c) => c.id === orgId)) setOrgId(clients[0]!.id);
  }, [demo, clients, orgId]);
  setOrgLocale(client);
  const locale = `${client?.taxPack ?? 'in'}-${client?.baseCurrency ?? 'INR'}-${client?.stateCode ?? ''}`;
  useEffect(() => notifyOrgLocale(), [locale]);
  const navigate = useNavigate();
  // Global QBO shortcuts (document 05); form-level ones live in the forms, search (Ctrl+Alt+F, /)
  // in GlobalSearch.
  useHotkeys({
    'ctrl+alt+a': () => void navigate({ to: '/accounting/chart-of-accounts' }),
    'ctrl+alt+c': () => void navigate({ to: '/sales/customers' }),
    'ctrl+alt+i': () => void navigate({ to: '/sales/invoices/new' }),
    'ctrl+alt+r': () => void navigate({ to: '/sales/receive-payment/new' }),
    'ctrl+alt+e': () => void navigate({ to: '/sales/quotes/new' }),
    'ctrl+alt+v': () => void navigate({ to: '/expenses/vendors' }),
    'ctrl+alt+/': () => void navigate({ to: '/help/shortcuts' }),
  });
  return (
    <SidebarProvider>
      <AppSidebar isStaff={me.data?.isStaff ?? true} />
      <SidebarInset className="min-w-0">
        {demo ? <DemoBanner persona={demo} /> : null}
        <TopBar />
        <main key={locale} className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </SidebarInset>
      <Toaster />
      <GlobalDrawers />
    </SidebarProvider>
  );
}
